import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SupabaseContext } from '../App';

function Quiz() {
  const { supabase, session } = useContext(SupabaseContext);
  const { code } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [quizId, setQuizId] = useState(null);
  const [attemptLimit, setAttemptLimit] = useState(false);

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        const { data: quiz, error: quizError } = await supabase
          .from('quizzes')
          .select('id, time_limit, attempt_limit')
          .eq('code', code)
          .single();
        if (quizError) throw new Error('Invalid quiz code.');

        setQuizId(quiz.id);
        setAttemptLimit(quiz.attempt_limit);
        if (quiz.time_limit) setTimeLeft(quiz.time_limit * 60);

        if (session) {
          const { data: history, error: historyError } = await supabase
            .from('quiz_history')
            .select('id')
            .eq('quiz_id', quiz.id)
            .eq('user_id', session.user.id);
          if (historyError) throw historyError;

          if (quiz.attempt_limit && history.length > 0) {
            alert('You have already attempted this quiz, and it allows only one attempt.');
            navigate('/history');
            return;
          }
        }

        const { data: questionsData, error: questionsError } = await supabase
          .from('questions')
          .select('id, question, a, b, c, d, correct')
          .eq('quiz_id', quiz.id);
        if (questionsError) throw questionsError;

        setQuestions(questionsData);
      } catch (error) {
        alert('Error: ' + error.message);
        navigate('/');
      }
    };
    fetchQuiz();
  }, [supabase, session, code, navigate]);

  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0 || isSubmitted) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit('TimeUp');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, isSubmitted]);

  const formatTime = (seconds) => {
    if (seconds === null) return 'No Time Limit';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleAnswerChange = (questionId, option) => {
    setAnswers((prev) => ({ ...prev, [questionId]: option }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = async (trigger) => {
    if (isSubmitted) return;

    setIsSubmitted(true);
    let score = 0;
    questions.forEach((q) => {
      if (answers[q.id] === q.correct) score++;
    });

    if (session) {
      try {
        const { error } = await supabase.from('quiz_history').insert([
          {
            quiz_id: quizId,
            user_id: session.user.id,
            score,
          },
        ]);
        if (error) throw error;
      } catch (error) {
        console.error('Error saving quiz history:', error.message);
      }
    }

    alert(`Quiz submitted! Your score: ${score}/${questions.length}`);
    navigate('/history');
  };

  if (questions.length === 0) {
    return <div className="page-container"><div className="auth-card"><h2>Loading...</h2></div></div>;
  }

  const currentQuestion = questions[currentQuestionIndex];
  const options = ['a', 'b', 'c', 'd'];

  return (
    <div className="page-container">
      <div className="auth-card">
        <h2>Quiz: {code}</h2>
        <p>Time Left: {formatTime(timeLeft)}</p>
        <p>Attempts: {attemptLimit ? 'Single Attempt' : 'Multiple Attempts'}</p>
        <div className="question-card">
          <h3>Question {currentQuestionIndex + 1}/{questions.length}</h3>
          <p>{currentQuestion.question}</p>
          {options.map((option) => (
            <label key={option} className="radio-label">
              <input
                type="radio"
                name={`question-${currentQuestion.id}`}
                value={option}
                checked={answers[currentQuestion.id] === option}
                onChange={() => handleAnswerChange(currentQuestion.id, option)}
                disabled={timeLeft === 0 || isSubmitted}
              />
              {currentQuestion[option]}
            </label>
          ))}
        </div>
        <div className="button-group">
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0 || timeLeft === 0 || isSubmitted}
            className="secondary-btn"
          >
            Previous
          </button>
          <button
            onClick={handleNext}
            disabled={currentQuestionIndex === questions.length - 1 || timeLeft === 0 || isSubmitted}
            className="secondary-btn"
          >
            Next
          </button>
          {currentQuestionIndex === questions.length - 1 && (
            <button
              onClick={() => handleSubmit('Manual')}
              disabled={timeLeft === 0 || isSubmitted}
            >
              Submit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Quiz;